<template>
  <v-container ref="container" style="max-width: 95%;">
    <h1>Credit Assessment Result: {{ uniqueId }}</h1>
    <v-simple-table
      :height="tableHeight"
      fixed-header
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th v-for="(header, index) in headers" :key="index">
              {{ header.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in displayData" :key="rowIndex">
            <td
              v-for="(cell, cellIndex) in row"
              :key="cellIndex"
              :rowspan="cell.rowspan || 1"
              :colspan="cell.colspan || 1"
              :style="getCellStyle(cell)"
              @click="openEditDialog('data_compare', rowIndex, cellIndex, cell)"
              style="cursor: pointer;"
            >
              {{ cell.value }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="400px">
      <v-card>
        <v-card-title>Edit Cell Value</v-card-title>
        <v-card-text>
          <v-form ref="editForm" @submit.prevent="updateValue">
            <v-text-field
              v-model="editCell.newValue"
              label="New Value"
              outlined
              dense
              :rules="[v => !!v || 'Value is required']"
              @keyup.enter="updateValue"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="updateValue">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container></template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      taskId: this.$route.params.id,
      uniqueId: this.$route.params.uniqueId,
      loading: false,
      fieldDisplaySeq: [
        {},
        {name: "Revenue", field: ["revenue"]},
        {name: "COGS", field: ["cogs"]},
        {name: "Gross Profit", field: ["gross_profit"]},
        {name: "Profit of the year", field: ["profit_of_the_year"]},
        {name: "GP%", field: ["gp"], percentage: true},
        {name: "NP%", field: ["np"], percentage: true},
        {name: "Current Assets", field: ["current_assets"]},
        {name: "Current Liabilities", field: ["current_liabilities"]},
        {name: "Total Assets", field: ["total_assets"]},
        {name: "Total Liabilities", field: ["total_liabilities"]},
        {name: "Net Equity", field: ["equity"]},
        {name: "Current ratio", field: ["current_ratio"]},
        {name: "Net working capital", field: ["net_working_capital"]},
        {name: "Debt ratio", field: ["debt_ratio"], percentage: true},
        {name: "Leverage ratio", field: ["leverage_ratio"]},
        {name: "DIO: Day’s inventory outstanding", field: ["dio"]},
        {name: "DSO: Days sales outstanding", field: ["dso"]},
        {name: "DPO: Day’s payable outstanding.", field: ["dpo"]},
        {name: "(CCC): Cash Conversion Cycle", field: ["ccc"]},
        {},
        {name: "", constant: "Amount HK$"},
        {name: "Fixed assets", field: ["non_current_assets"]},
        {name: "Current assets", field: ["current_assets"]},
        {name: "Non current liabilites", field: ["non_current_liabilities"]},
        {name: "Current liabilities", field: ["current_liabilities"]},
        {name: "Opening Inventory", field: ["inventory"], lastYear: true},
        {name: "Closing Inventory", field: ["inventory"]},
        {name: "Cost of Goods Sold", field: ["cogs"]},
        {name: "Opening Account Receivable", field: ["account_receivable"], lastYear: true},
        {name: "Closing Account Receivable", field: ["account_receivable"]},
        {name: "Sales", field: ["revenue"]},
        {name: "Opening Account Payable", field: ["account_payable"], lastYear: true},
        {name: "Closing Account Payable", field: ["account_payable"]},
        {},
        {},
        {name: "DIO = (Average Inventory / Cost of Goods Sold ) * No. of days", field: ["dio"]},
        {name: "", field: ["score", "dio"], grade: true},
        {},
        {name: "DSO = (Average Account Receivable / Total Credit Sales) * No of Days", field: ["dso"]},
        {name: "", field: ["score", "dso"], grade: true},
        {},
        {name: "DPO = (Average Account Payable / Cost of Goods Sold) * No of Days", field: ["dpo"]},
        {name: "", field: ["score", "dpo"], grade: true},
        {},
        {name: "Cash Conversion Cycle (CCC) = DIO + DSO – DPO", field: ["ccc"]},
        {name: "", field: ["score", "ccc"], grade: true},
        {},
        {name: "Transaction per year", field: ["transaction_per_year"]},
        {},
        {name: "Liquidity ratio = Total assets / current liabilities", field: ["liquidity_ratio"]},
        {name: "", field: ["score", "liquidity_ratio"], grade: true},
        {},
        {name: "Current ratio = Current assets / current liabilities", field: ["current_ratio"]},
        {name: "", field: ["score", "current_ratio"], grade: true},
        {},
        {name: "Result"},
        {name: "Granted Credit limit", field: ["granted_credit"]},
        {name: "Granted length of trade", field: ["granted_length_of_trade"]},
        {name: "Scorecard", field: ["score_card"]},
      ],
      headers: [
        { text: ''},
        { text: ''},
        { text: ''},
        { text: ''},
      ],
      tableData: {},
      displayData: [],
      outputUrl: "",
      isCompleted: false,
      containerHeight: 0,
      // New data properties for editing
      editDialog: false,
      editCell: {
        tableType: '', // 'data_compare' or 'item_listing'
        groupIndex: null, // For item_listing, the group index
        rowIndex: null, // Row index within group (item_listing) or table (data_compare)
        cellIndex: null, // Cell index within row
        newValue: '' // The new value being edited
      }
    }
  },
  computed: {
    tableHeight() {
      const offset = 140;
      return `calc(100vh - ${offset}px)`;
    }
  },
  methods: {
    async fetchResult() {
      this.loading = true
      try {
        const response = await this.$http.get(`/api/tasks/${this.taskId}/result`)
        this.tableData = response.data
        this.convertToDisplayData()
        // this.initializeItemListing()
        this.isCompleted = true
      } catch (error) {
        console.error('Error fetching task result:', error)
        alert('Failed to load task result')
      }
      this.loading = false
    },
    getNestedValue(data, keyPath) {
      let curData = data
      for (const key of keyPath) {
        if (curData[key]) {
          curData = curData[key]
        }
        else {
          return null
        }
      }
      return curData
    },
    convertDisplayFormat(value, isPercentage=false, digits=2) {
      try {
        if (isPercentage) {
          value = value * 100
        }
        value = value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
        if (isPercentage) {
          value += "%"
        }        
      } catch (error) {
        // console.error(error);
      }
      return value
    },
    convertToDisplayData() {
      this.displayData = []
      let data = this.tableData.data

      this.displayData.push([
        { value: "Company: " },
        { value: this.tableData.company, colspan: 3},
      ])
      this.headers[1].text = data[0].year
      this.headers[3].text = data[1].year
      for (let displayInfo of this.fieldDisplaySeq) {
        let rowData = Array(4)
        for (var i = 0; i < 4; i++) {
            rowData[i] = { value: "" };
        }
        if (displayInfo.name) {
          rowData[0].value = displayInfo.name
        }
        if (displayInfo.field) {
          let curData = "-"
          let lastData = "-"
          if (displayInfo.lastYear) {
            curData = this.getNestedValue(data[1], displayInfo.field) ?? "-"
          }
          else {
            curData = this.getNestedValue(data[0], displayInfo.field) ?? "-"
            lastData = this.getNestedValue(data[1], displayInfo.field) ?? "-"
          }
          if (displayInfo.grade) {
            rowData[2].value = curData
            switch (curData) {
              case 5: rowData[1].value = "A"; break;
              case 4: rowData[1].value = "B"; break;
              case 3: rowData[1].value = "C"; break;
              case 2: rowData[1].value = "D"; break;
              case 1: rowData[1].value = "E"; break;
            }
          }
          else {
            rowData[1].value = this.convertDisplayFormat(curData, displayInfo.percentage)
            rowData[3].value = this.convertDisplayFormat(lastData, displayInfo.percentage)
          }
        }
        else if (displayInfo.constant) {
            rowData[1].value = displayInfo.constant
            rowData[3].value = displayInfo.constant
        }

        this.displayData.push(rowData)
      }
    },
    getCellStyle(cell) {
      const style = {
        backgroundColor: cell.highlight_color ? `#${cell.highlight_color}` : ''
      }
      if (cell.borders) {
        const styleMap = {
          thin: '1px solid',
          medium: '2px solid',
          thick: '3px solid'
        }
        const defaultBorderColor = '#000'
        if (cell.borders.left) {
          style.borderLeft = `${styleMap[cell.borders.left.style] || '1px solid'} ${
            cell.borders.left.color ? `#${cell.borders.left.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.top) {
          style.borderTop = `${styleMap[cell.borders.top.style] || '1px solid'} ${
            cell.borders.top.color ? `#${cell.borders.top.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.bottom) {
          style.borderBottom = `${styleMap[cell.borders.bottom.style] || '1px solid'} ${
            cell.borders.bottom.color ? `#${cell.borders.bottom.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.right) {
          style.borderRight = `${styleMap[cell.borders.right.style] || '1px solid'} ${
            cell.borders.right.color ? `#${cell.borders.right.color}` : defaultBorderColor
          }`
        }
      }
      return style
    },
    updateContainerHeight() {
      this.containerHeight = this.$refs.container ? this.$refs.container.clientHeight : 0
    },
    // New method to open the edit dialog
    openEditDialog(tableType, groupIndex, cellIndex, cell, rowIndex = null) {
      if (cell.field) {
        this.editCell = {
          tableType,
          cell,
          groupIndex,
          rowIndex: rowIndex !== null ? rowIndex : groupIndex, // For data_compare, groupIndex is rowIndex
          cellIndex,
          newValue: cell.value
        }
        this.editDialog = true
      }
    },
    // New method to update the cell value
    async updateValue() {
      try {
        const { tableType, cell, groupIndex, rowIndex, cellIndex, newValue } = this.editCell
        await this.$http.post(`/api/tasks/${this.taskId}/update_value`, {
          field: cell.field,
          value: newValue
        })
        this.editDialog = false
        await this.fetchResult() // Refresh the table data
      } catch (error) {
        console.error('Error updating cell value:', error)
        alert('Failed to update cell value')
      }
    },
    // New method to handle dialog closing and form reset
    closeDialog() {
      this.editDialog = false
      if (this.$refs.editForm) {
        this.$refs.editForm.reset()
      }
    }
  },
  mounted() {
    this.fetchResult()
    this.outputUrl = `${this.$http.defaults.baseURL}/api/tasks/${this.$route.params.id}/output`
    // window.addEventListener('resize', this.updateContainerHeight)
    // this.updateContainerHeight()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerHeight)
  }
}
</script>

<style scoped>
::v-deep .v-data-table th:nth-child(1),
::v-deep .v-data-table td:nth-child(1) {
  width: 40% !important;
  max-width: 40% !important;
  text-align: left !important;
}

::v-deep .v-data-table th:nth-child(2),
::v-deep .v-data-table th:nth-child(3),
::v-deep .v-data-table th:nth-child(4) {}
::v-deep .v-data-table td:nth-child(2),
::v-deep .v-data-table td:nth-child(3),
::v-deep .v-data-table td:nth-child(4) {
  width: 20% !important;
  max-width: 20% !important;
}

::v-deep .v-data-table th {
  text-align: center !important;
}

::v-deep .v-data-table td {
  text-align: right !important;
}

td {
  border: 1px solid #ddd;
  padding: 8px;
}
td[style*="background-color"] {
  color: #fff;
}
::v-deep .v-data-table table {
  border-collapse: collapse;
}

</style>
